import { useEffect, useRef, useState } from "react";
import "@/App.css";
import "@/styles/en-purple-palette.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages - Marketplace
import HomePage from "@/pages/HomePage";
import BrowsePage from "@/pages/BrowsePage";
import VendorProfilePage from "@/pages/VendorProfilePage";
import AuthPage from "@/pages/AuthPage";
import IntentPickerPage from "@/pages/IntentPickerPage";
import AuthCallback from "@/pages/AuthCallback";
import VendorOnboarding from "@/pages/VendorOnboarding";
import VendorPayoutWizard from "@/pages/VendorPayoutWizard";
import VendorEarningsDashboard from "@/pages/VendorEarningsDashboard";
import ConsumerDashboard from "@/pages/ConsumerDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import SupporterDashboard from "@/pages/SupporterDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import MessagesPage from "@/pages/MessagesPage";
import BookingPage from "@/pages/BookingPage";
import TrustSafetyPage from "@/pages/TrustSafetyPage";
import FAQPage from "@/pages/FAQPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import RefundPage from "@/pages/RefundPage";
import PricingPage from "@/pages/PricingPage";
import LearnPage from "@/pages/LearnPage";
import MyOrders from "@/pages/MyOrders";
import OrderDetailBuyer from "@/components/orders/OrderDetail";

// Pages - Website Builder
import WebsiteBuilderLayout from "@/pages/website-builder/WebsiteBuilderLayout";
import DashboardHome from "@/pages/website-builder/DashboardHome";
import DashboardOrders from "@/pages/website-builder/DashboardOrders";
import DashboardProducts from "@/pages/website-builder/DashboardProducts";
import DashboardCustomers from "@/pages/website-builder/DashboardCustomers";
import { DesktopOnlyGate } from "@/components/MobileNav";
import DashboardDiscounts from "@/pages/website-builder/DashboardDiscounts";
import DashboardContent from "@/pages/website-builder/DashboardContent";
import DashboardMedia from "@/pages/website-builder/DashboardMedia";
import DashboardAnalytics from "@/pages/website-builder/DashboardAnalytics";
import DashboardMessages from "@/pages/website-builder/DashboardMessages";
import DashboardFinances from "@/pages/website-builder/DashboardFinances";
import DashboardBookings from "@/pages/website-builder/DashboardBookings";
import DashboardDomains from "@/pages/website-builder/DashboardDomains";
import DashboardTemplates from "@/pages/website-builder/DashboardTemplates";
import DashboardSettings from "@/pages/website-builder/DashboardSettings";
import DashboardApprovals from "@/pages/website-builder/DashboardApprovals";
import SettingsAccountControls from "@/pages/website-builder/settings/SettingsAccountControls";
import { SettingsSubPage as SettingsSubPageWithSidebar } from "@/pages/website-builder/SettingsHub";
import SettingsHubLayout from "@/pages/website-builder/settings/SettingsHubLayout";
import SettingsHubIndex from "@/pages/website-builder/settings/SettingsHubIndex";
import SettingsBilling from "@/pages/website-builder/settings/SettingsBilling";
import SettingsIntegrations from "@/pages/website-builder/settings/SettingsIntegrations";
import SettingsPaymentsBranding from "@/pages/website-builder/settings/SettingsPaymentsBranding";
import InvoicingDashboard from "@/pages/website-builder/invoicing/InvoicingDashboard";
import InvoiceBuilder from "@/pages/website-builder/invoicing/InvoiceBuilder";
import ClientsList from "@/pages/website-builder/invoicing/ClientsList";
import ClientDetail from "@/pages/website-builder/invoicing/ClientDetail";
import SettingsTeam from "@/pages/website-builder/settings/SettingsTeam";
import SettingsDonations from "@/pages/website-builder/settings/SettingsDonations";
import SettingsShipping from "@/pages/website-builder/settings/SettingsShipping";
import SettingsCartRecovery from "@/pages/website-builder/settings/SettingsCartRecovery";
import SettingsFavicon from "@/pages/website-builder/settings/SettingsFavicon";
import SettingsSiteAvailability from "@/pages/website-builder/settings/SettingsSiteAvailability";
import SettingsSiteLanguages from "@/pages/website-builder/settings/SettingsSiteLanguages";
import SettingsCookies from "@/pages/website-builder/settings/SettingsCookies";
import SettingsBlog from "@/pages/website-builder/settings/SettingsBlog";
import SettingsWorkspace from "@/pages/website-builder/settings/SettingsWorkspace";
import SettingsPayments from "@/pages/website-builder/settings/SettingsPayments";
import SettingsBrandedEmails from "@/pages/website-builder/settings/SettingsBrandedEmails";
import SettingsSEO from "@/pages/website-builder/settings/SettingsSEO";
import SettingsCheckout from "@/pages/website-builder/settings/SettingsCheckout";
// Iter 191t · Msg 598 — new settings pages
import SettingsPixels from "@/pages/website-builder/settings/SettingsPixels";
import SettingsPrivacy from "@/pages/website-builder/settings/SettingsPrivacy";
import SettingsSecurity from "@/pages/website-builder/settings/SettingsSecurity";
import SettingsLocation from "@/pages/website-builder/settings/SettingsLocation";
import SettingsTaxes from "@/pages/website-builder/settings/SettingsTaxes";
import SettingsConfirmEmail from "@/pages/website-builder/settings/SettingsConfirmEmail";
// Iter 191u · Customer Accounts (vendor settings + storefront pages)
import SettingsCustomerAccounts from "@/pages/website-builder/settings/SettingsCustomerAccounts";
import SettingsNotifications from "@/pages/website-builder/settings/SettingsNotifications";
import SettingsPolicies from "@/pages/website-builder/settings/SettingsPolicies";
import SettingsProductReviews from "@/pages/website-builder/settings/SettingsProductReviews";
import BuyerAccountPage, { BuyerVerifyPage } from "@/pages/storefront/BuyerAccountPage";
import DashboardHelp from "@/pages/website-builder/DashboardHelp";
import DashboardSignups from "@/pages/website-builder/DashboardSignups";
import DashboardPages from "@/pages/website-builder/DashboardPages";
import DashboardStyles from "@/pages/website-builder/DashboardStyles";
import DashboardAssets from "@/pages/website-builder/DashboardAssets";
import DashboardSEOAIO from "@/pages/website-builder/DashboardSEOAIO";
import DashboardCustomCSS from "@/pages/website-builder/DashboardCustomCSS";
import DashboardProductsServices from "@/pages/website-builder/DashboardProductsServices";
import DashboardInvoicing from "@/pages/website-builder/DashboardInvoicing";
import DashboardSellingTools from "@/pages/website-builder/DashboardSellingTools";
import DashboardContentMemberships from "@/pages/website-builder/DashboardContentMemberships";
import DashboardCampaigns from "@/pages/website-builder/DashboardCampaigns";
import DashboardDonations from "@/pages/website-builder/DashboardDonations";
import DashboardPayLinks from "@/pages/website-builder/DashboardPayLinks";

// Pages - Social
import FeedPage from "@/pages/FeedPage";
import JobRequestsPage from "@/pages/JobRequestsPage";
import JobRequestDetailPage from "@/pages/JobRequestDetailPage";
import NewJobRequestPage from "@/pages/NewJobRequestPage";
import EventsPage from "@/pages/EventsPage";
import NewEventPage from "@/pages/NewEventPage";
import EventDetailPage from "@/pages/EventDetailPage";

// Pages - Master Dashboard (Platform Owner)
import MasterLayout from "@/pages/master/MasterLayout";
import MasterLoginPage from "@/pages/master/MasterLoginPage";
import MasterHome from "@/pages/master/MasterHome";
import MasterSites from "@/pages/master/MasterSites";
import MasterRecovery from "@/pages/master/MasterRecovery";
import MasterSiteNew from "@/pages/master/MasterSiteNew";
import MasterSiteEditor from "@/pages/master/MasterSiteEditor";
import MasterSiteBuilderEdit from "@/pages/master/MasterSiteBuilderEdit";
import MasterBilling from "@/pages/master/MasterBilling";
import MasterDomains from "@/pages/master/MasterDomains";
import MasterSupport from "@/pages/master/MasterSupport";
import MasterCompliance from "@/pages/master/MasterCompliance";
import MasterAIRedesign from "@/pages/master/MasterAIRedesign";
import MasterTemplates from "@/pages/master/MasterTemplates";
import MasterUsers from "@/pages/master/MasterUsers";
import MasterUserDetail from "@/pages/master/MasterUserDetail";
import MasterForms from "@/pages/master/MasterForms";
import MasterFormBuilder from "@/pages/master/MasterFormBuilder";
import MasterFormSubmissions from "@/pages/master/MasterFormSubmissions";
import MasterDeletedAccounts from "@/pages/master/MasterDeletedAccounts";
import PublicFormPage from "@/pages/PublicFormPage";
import PublicSitePage from "@/pages/PublicSitePage";
import MasterEmailCenter from "@/pages/master/MasterEmailCenter";
import MasterTransactions from "@/pages/master/MasterTransactions";
import MasterComplaints from "@/pages/master/MasterComplaints";
import MasterSignupErrors from "@/pages/master/MasterSignupErrors";
import MasterIntegrations from "@/pages/master/MasterIntegrations";
import MasterEINReviews from "@/pages/master/MasterEINReviews";
import MasterDigest from "@/pages/master/MasterDigest";
import MasterFeedback from "@/pages/master/MasterFeedback";
import HealthPage from "@/pages/HealthPage";
import HireDesigner from "@/pages/website-builder/HireDesigner";
import TemplatePreview from "@/pages/master/TemplatePreview";
import MarketplaceEditor from "@/pages/master/MarketplaceEditor";
import MarketplaceOverview from "@/pages/master/MarketplaceOverview";
import WebsiteBuilderAuth from "@/pages/WebsiteBuilderAuth";
import AICreator from "@/pages/master/AICreator";
import LegalPage, { LegalHub } from "@/pages/LegalPage";
import InviteFriends from "@/pages/InviteFriends";
import FoundingMembersPage from "@/pages/FoundingMembersPage";
import FeedbackPage from "@/pages/FeedbackPage";
import FoundingNonProfitsPage from "@/pages/FoundingNonProfitsPage";
import MarketplacePage from "@/pages/MarketplacePage";

// Context
import { AuthProvider } from "@/context/AuthContext";
import { MasterAuthProvider } from "@/context/MasterAuthContext";
import { CartProvider } from "@/context/CartContext";
import { MarketplaceCartProvider } from "@/context/MarketplaceCartContext";
import { CartDrawerProvider, CartDrawer } from "@/components/storefront/CartDrawer";
import StorefrontCheckoutPage from "@/pages/StorefrontCheckoutPage";
import ShopPage from "@/pages/ShopPage";
import ItemDetailPage from "@/pages/ItemDetailPage";
import ItemCheckoutPage from "@/pages/ItemCheckoutPage";

// Components
import ProtectedRoute from "@/components/ProtectedRoute";
import useSiteVisitTracking from "@/hooks/useSiteVisitTracking";
import useMetaPixel from "@/hooks/useMetaPixel";
import CookieConsentBar from "@/components/CookieConsentBar";

function AppRouter() {
  const location = useLocation();
  useSiteVisitTracking();
  useMetaPixel();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle session_id from Google OAuth before rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* ========== MARKETPLACE ROUTES ========== */}
      <Route path="/" element={<HomePage />} />
      <Route path="/feed" element={<FeedPage />} />
      <Route path="/jobs" element={<JobRequestsPage />} />
      <Route path="/jobs/new" element={<NewJobRequestPage />} />
      <Route path="/jobs/:id" element={<JobRequestDetailPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/events/new" element={<NewEventPage />} />
      <Route path="/events/:id" element={<EventDetailPage />} />
      <Route path="/forms/:slug" element={<PublicFormPage />} />
      {/* Iter 175h — Public live-preview renderer for builder sites. */}
      <Route path="/site/:subdomain" element={<PublicSitePage />} />
      {/* Iter 189 — Public storefront checkout (embedded Stripe Payment Element). */}
      <Route path="/checkout" element={<StorefrontCheckoutPage />} />
      <Route path="/site/:subdomain/checkout" element={<StorefrontCheckoutPage />} />
      {/* Iter 175i — Master "Edit Site" transit page (token swap → /website-builder). */}
      <Route path="/master/sites/:siteId/builder" element={<MasterSiteBuilderEdit />} />
      <Route path="/browse" element={<BrowsePage />} />
      {/* Marketplace Items (Shopify-style) — public shop, item detail, checkout */}
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/item/:itemId" element={<ItemDetailPage />} />
      <Route path="/shop/checkout" element={<ItemCheckoutPage />} />
      <Route path="/learn" element={<LearnPage />} />
      <Route path="/my-orders" element={<MyOrders />} />
      <Route path="/orders/:orderId" element={
        <div className="min-h-screen bg-[#0B0D2B]">
          <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
            <OrderDetailBuyer />
          </div>
        </div>
      } />
      <Route path="/vendor/:vendorId" element={<VendorProfilePage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/settings/confirm-email" element={<SettingsConfirmEmail />} />
      {/* Iter 191u — Storefront buyer accounts (per vendor subdomain) */}
      <Route path="/site/:subdomain/account" element={<BuyerAccountPage />} />
      <Route path="/site/:subdomain/account/sign-in" element={<BuyerAccountPage />} />
      <Route path="/site/:subdomain/account/verify" element={<BuyerVerifyPage />} />
      <Route path="/onboarding/intent" element={<IntentPickerPage />} />
      <Route path="/health" element={<HealthPage />} />
      <Route path="/status" element={<HealthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/create-website" element={<WebsiteBuilderAuth />} />
      <Route path="/onboarding/vendor" element={
        <ProtectedRoute>
          <VendorOnboarding />
        </ProtectedRoute>
      } />
      <Route path="/vendor/onboarding" element={
        <ProtectedRoute>
          <VendorOnboarding />
        </ProtectedRoute>
      } />
      <Route path="/vendor/setup-payouts" element={
        <ProtectedRoute>
          <VendorPayoutWizard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/vendor/earnings" element={
        <ProtectedRoute>
          <VendorEarningsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/consumer/*" element={
        <ProtectedRoute>
          <ConsumerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/vendor/*" element={
        <ProtectedRoute>
          <VendorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/supporter/*" element={
        <ProtectedRoute>
          <SupporterDashboard />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/messages/:userId" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/booking/:serviceId" element={
        <ProtectedRoute>
          <BookingPage />
        </ProtectedRoute>
      } />
      <Route path="/trust-safety" element={<TrustSafetyPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/refunds" element={<RefundPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/founding-members" element={<FoundingMembersPage />} />
      <Route path="/founding-vendors" element={<FoundingMembersPage />} />
      <Route path="/founding-family" element={<FoundingMembersPage />} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route path="/founding-nonprofits" element={<FoundingNonProfitsPage />} />
      <Route path="/founding-non-profits" element={<FoundingNonProfitsPage />} />
      <Route path="/p/:slug" element={<MarketplacePage />} />
      <Route path="/legal" element={<LegalHub />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/invite" element={
        <ProtectedRoute>
          <InviteFriends />
        </ProtectedRoute>
      } />

      {/* ========== WEBSITE BUILDER ROUTES ========== */}
      <Route path="/website-builder" element={
        <ProtectedRoute>
          <WebsiteBuilderLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="orders" element={<DashboardOrders />} />
        <Route path="products" element={<DashboardProducts />} />
        <Route path="customers" element={<DashboardCustomers />} />
        <Route path="discounts" element={<DashboardDiscounts />} />
        <Route path="content" element={<DesktopOnlyGate feature="Content Editor"><DashboardContent /></DesktopOnlyGate>} />
        <Route path="media" element={<DashboardMedia />} />
        <Route path="analytics" element={<DashboardAnalytics />} />
        <Route path="messages" element={<DashboardMessages />} />
        <Route path="finances" element={<DashboardFinances />} />
        <Route path="bookings" element={<DashboardBookings />} />
        <Route path="domains" element={<DesktopOnlyGate feature="Domain Manager"><DashboardDomains /></DesktopOnlyGate>} />
        <Route path="templates" element={<DesktopOnlyGate feature="Template Editor"><DashboardTemplates /></DesktopOnlyGate>} />
        <Route path="settings" element={<SettingsHubLayout />}>
          <Route index element={<SettingsHubIndex />} />
          <Route path="account-controls" element={<SettingsAccountControls />} />
          <Route path="general" element={<DashboardSettings />} />
          <Route path="billing" element={<SettingsBilling />} />
          <Route path="integrations" element={<SettingsIntegrations />} />
          <Route path="payments-branding" element={<SettingsPaymentsBranding />} />
          <Route path="payments" element={<SettingsPayments />} />
          <Route path="branded-emails" element={<SettingsBrandedEmails />} />
          <Route path="seo" element={<SettingsSEO />} />
          <Route path="checkout" element={<SettingsCheckout />} />
          <Route path="team" element={<SettingsTeam />} />
          <Route path="shipping" element={<SettingsShipping />} />
          <Route path="cart-recovery" element={<SettingsCartRecovery />} />
          <Route path="donations" element={<SettingsDonations />} />
          <Route path="favicon" element={<SettingsFavicon />} />
          {/* Iter 191t · Msg 598 — new Settings surfaces */}
          <Route path="pixels" element={<SettingsPixels />} />
          <Route path="privacy" element={<SettingsPrivacy />} />
          <Route path="security" element={<SettingsSecurity />} />
          <Route path="location" element={<SettingsLocation />} />
          <Route path="taxes" element={<SettingsTaxes />} />
          <Route path="customer-accounts" element={<SettingsCustomerAccounts />} />
          <Route path="notifications" element={<SettingsNotifications />} />
          <Route path="policies" element={<SettingsPolicies />} />
          <Route path="reviews" element={<SettingsProductReviews />} />
          <Route path="confirm-email" element={<SettingsConfirmEmail />} />
          <Route path="site-availability" element={<SettingsSiteAvailability />} />
          <Route path="languages" element={<SettingsSiteLanguages />} />
          <Route path="cookies" element={<SettingsCookies />} />
          <Route path="blog" element={<SettingsBlog />} />
          <Route path="workspace" element={<SettingsWorkspace />} />
          <Route path="donations/:fundId" element={<SettingsDonations />} />
          <Route path="domains" element={<DesktopOnlyGate feature="Domain Manager"><DashboardDomains /></DesktopOnlyGate>} />
          <Route path=":section" element={<SettingsSubPageWithSidebar />} />
        </Route>
        <Route path="approvals" element={<DashboardApprovals />} />
        <Route path="payments/onboard-complete" element={<SettingsPaymentsBranding />} />
        <Route path="payments/onboard-refresh" element={<SettingsPaymentsBranding />} />
        <Route path="invoicing" element={<InvoicingDashboard />} />
        <Route path="invoicing/dashboard" element={<InvoicingDashboard />} />
        <Route path="invoicing/invoices/new" element={<InvoiceBuilder />} />
        <Route path="invoicing/invoices/:id/edit" element={<InvoiceBuilder />} />
        <Route path="invoicing/clients" element={<ClientsList />} />
        <Route path="invoicing/clients/:id" element={<ClientDetail />} />
        <Route path="hire-designer" element={<HireDesigner />} />
        <Route path="help" element={<DashboardHelp />} />
        <Route path="signups" element={<DashboardSignups />} />

        {/* Squarespace-style Website sub-nav */}
        <Route path="pages" element={<DesktopOnlyGate feature="Pages"><DashboardPages /></DesktopOnlyGate>} />
        <Route path="styles" element={<DesktopOnlyGate feature="Styles"><DashboardStyles /></DesktopOnlyGate>} />
        <Route path="assets" element={<DesktopOnlyGate feature="Assets"><DashboardAssets /></DesktopOnlyGate>} />
        <Route path="seo" element={<DesktopOnlyGate feature="SEO/AIO"><DashboardSEOAIO /></DesktopOnlyGate>} />
        <Route path="custom-css" element={<DesktopOnlyGate feature="Custom CSS"><DashboardCustomCSS /></DesktopOnlyGate>} />

        {/* Products & Services hub */}
        <Route path="products-services" element={<DashboardProductsServices />} />
        <Route path="selling-tools" element={<DashboardSellingTools />} />

        {/* New top-level pages */}
        <Route path="invoicing" element={<DashboardInvoicing />} />
        <Route path="invoices" element={<DashboardInvoicing />} />
        <Route path="pay-links" element={<DashboardPayLinks />} />
        <Route path="projects" element={<DashboardInvoicing />} />
        <Route path="content-memberships" element={<DashboardContentMemberships />} />
        <Route path="campaigns" element={<DashboardCampaigns />} />
        <Route path="donations" element={<DashboardDonations />} />
      </Route>

      {/* ========== MASTER DASHBOARD ROUTES (Platform Owner) ========== */}
      <Route path="/master/login" element={<MasterLoginPage />} />
      <Route path="/master" element={<MasterLayout />}>
        <Route index element={<MasterHome />} />
        <Route path="sites" element={<MasterSites />} />
        {/* Iter 175g — Legacy /master/builder-sites was its own tab; now redirects
            to the unified Sites tab so deep links keep working. */}
        <Route path="builder-sites" element={<MasterSites />} />
        <Route path="recovery" element={<MasterRecovery />} />
        <Route path="users" element={<MasterUsers />} />
        <Route path="users/:userId" element={<MasterUserDetail />} />
        <Route path="forms" element={<MasterForms />} />
        <Route path="forms/builder" element={<MasterFormBuilder />} />
        <Route path="forms/builder/:formId" element={<MasterFormBuilder />} />
        <Route path="forms/:formId/submissions" element={<MasterFormSubmissions />} />
        <Route path="deleted-accounts" element={<MasterDeletedAccounts />} />
        <Route path="email-center" element={<MasterEmailCenter />} />
        <Route path="transactions" element={<MasterTransactions />} />
        <Route path="complaints" element={<MasterComplaints />} />
        <Route path="signup-errors" element={<MasterSignupErrors />} />
        <Route path="ein-reviews" element={<MasterEINReviews />} />
        <Route path="digest" element={<MasterDigest />} />
        <Route path="feedback" element={<MasterFeedback />} />
        <Route path="integrations" element={<MasterIntegrations />} />
        <Route path="sites/new" element={<MasterSiteNew />} />
        <Route path="sites/:siteId/edit" element={<MasterSiteEditor />} />
        {/* Iter 175g — Analytics merged into Overview (MasterHome). Deep links
            to /master/analytics still work and now render the Overview page. */}
        <Route path="analytics" element={<MasterHome />} />
        <Route path="billing" element={<MasterBilling />} />
        {/* Iter 175g — Billing Controls merged into Billing as a section. */}
        <Route path="billing/controls" element={<MasterBilling />} />
        <Route path="domains" element={<MasterDomains />} />
        <Route path="support" element={<MasterSupport />} />
        <Route path="compliance" element={<MasterCompliance />} />
        <Route path="ai-redesign" element={<MasterAIRedesign />} />
        <Route path="templates" element={<MasterTemplates />} />
        <Route path="marketplace" element={<MarketplaceEditor />} />
        <Route path="marketplace/overview" element={<MarketplaceOverview />} />
        <Route path="marketplace/pages" element={<MarketplaceEditor />} />
        <Route path="marketplace/ai" element={<AICreator />} />
      </Route>
      <Route path="/master/template/:templateId" element={<TemplatePreview />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <MasterAuthProvider>
        <CartProvider>
          <MarketplaceCartProvider>
            <CartDrawerProvider>
              <div className="App min-h-screen bg-stone-950">
                <BrowserRouter>
                  <AppRouter />
                  <CookieConsentBar />
                  <CartDrawer />
                </BrowserRouter>
                <Toaster position="top-right" richColors />
              </div>
            </CartDrawerProvider>
          </MarketplaceCartProvider>
        </CartProvider>
      </MasterAuthProvider>
    </AuthProvider>
  );
}

export default App;
